/**
 * Shared SEO helpers for route-level `head()` metadata.
 *
 * Canonical URLs use the non-www apex domain with no trailing slash:
 *   https://precisionpaverspools.com/<path>
 *
 * Route `head()` runs during SSR/prerender, so every title, meta description,
 * canonical link, and Open Graph tag is present in the initial HTML — not
 * injected by client JavaScript.
 */

export const SITE_URL = "https://precisionpaverspools.com";

/** Build a canonical URL for a path. Leading slash required, no trailing slash. */
export function canonical(path: string): string {
  const normalized = path.replace(/\/+$/, "");
  return `${SITE_URL}${normalized}`;
}

export interface RouteMeta {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  /** Open Graph content type. Defaults to "website"; use "article" for blog posts. */
  ogType?: string;
  jsonLd?: any | any[];
}

/** Build the standard `head()` meta/links/structured-data block for a route. */
export function buildHead(meta: RouteMeta) {
  const url = canonical(meta.path);
  const scripts = meta.jsonLd
    ? (Array.isArray(meta.jsonLd) ? meta.jsonLd : [meta.jsonLd]).map(
        (data) => ({
          type: "application/ld+json" as const,
          children: JSON.stringify(data),
        }),
      )
    : [];

  return {
    meta: [
      { title: meta.title },
      { name: "description", content: meta.description },
      { property: "og:title", content: meta.title },
      { property: "og:description", content: meta.description },
      { property: "og:type", content: meta.ogType || "website" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: meta.title },
      { name: "twitter:description", content: meta.description },
      ...(meta.ogImage
        ? [
            { property: "og:image", content: meta.ogImage },
            { name: "twitter:image", content: meta.ogImage },
          ]
        : []),
    ],
    links: [{ rel: "canonical", href: url }],
    scripts,
  };
}
